import React from 'react'
import { Button, Checkbox, Icon, Skeleton, Tag, Tooltip, Dropdown, Menu } from 'antd'
import { useTranslation } from 'react-i18next'
import AdCardHolder from '../styles/adCardStyle'

const TYPE_ICON = {
  image: 'picture',
  video: 'video-camera',
}

const STATUS_COLOR = {
  available: 'green',
  'in-use': 'blue',
  unavailable: 'red',
}

const STATUS_LABEL = {
  available: 'Available',
  'in-use': 'In use',
  unavailable: 'Not available',
}

const DragHandleIcon = () => (
  <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
    <circle cx="2" cy="2" r="1.4" />
    <circle cx="8" cy="2" r="1.4" />
    <circle cx="2" cy="8" r="1.4" />
    <circle cx="8" cy="8" r="1.4" />
    <circle cx="2" cy="14" r="1.4" />
    <circle cx="8" cy="14" r="1.4" />
  </svg>
)

const AdCard = ({ ad, viewMode = 'grid', selected, onToggleSelect, onPreview, menuItems, showStatus = false, actions, dragHandleProps, added = false }) => {
  const { t } = useTranslation()

  const overlay = menuItems ? (
    <Menu>
      {menuItems.map((item) => (
        <Menu.Item key={item.key} disabled={item.disabled} onClick={item.disabled ? undefined : item.onClick}>
          {item.label}
        </Menu.Item>
      ))}
    </Menu>
  ) : null

  const hasActions = actions && actions.length > 0
  return (
    <AdCardHolder
      className={`${viewMode === 'list' ? 'list-view' : 'grid-view'}${hasActions ? ' has-actions' : ''}`}
    >
      <div
        className={`thumbnail${onPreview ? ' clickable' : ''}`}
        onClick={() => onPreview && onPreview()}
        role={onPreview ? 'button' : undefined}
      >
        {onToggleSelect && (
          <Checkbox
            className="select-checkbox"
            checked={selected}
            onChange={onToggleSelect}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {dragHandleProps ? (
          <span
            className="drag-handle"
            title={t('Drag to add to version')}
            onClick={(e) => e.stopPropagation()}
            {...dragHandleProps}
          >
            <DragHandleIcon />
          </span>
        ) : (
          <Icon
            type={TYPE_ICON[ad.type] || 'file'}
            className="type-icon"
          />
        )}
        {ad.pending && (
          <Skeleton className="thumbnail-skeleton" active title={false} paragraph={{ rows: 1 }} />
        )}
        {ad.thumbnail && <img src={ad.thumbnail} alt={ad.name} draggable={false} />}
        {ad.width && ad.height && (
          <span className="dimensions-tag">{`${ad.width} x ${ad.height}`}</span>
        )}
        {showStatus && added && (
          <Tag className="status-tag" color="blue">
            {t('Added').toUpperCase()}
          </Tag>
        )}
        {showStatus && !added && ad.status && (
          <Tag className="status-tag" color={STATUS_COLOR[ad.status]}>
            {t(STATUS_LABEL[ad.status] || ad.status).toUpperCase()}
          </Tag>
        )}
      </div>

      <div className="info">
        <div className="title-row">
          <Tooltip title={ad.name}>
            <span className="name">{ad.name}</span>
          </Tooltip>
          {overlay && (
            <Dropdown overlay={overlay} trigger={['click']}>
              <Icon type="more" className="menu-icon" />
            </Dropdown>
          )}
        </div>
        <div className="meta">
          <span className="type">{t(ad.type === 'video' ? 'Video' : 'Image')}</span>
          <span className="date">{ad.date}</span>
        </div>
        {actions && actions.length > 0 && (
          <div className="card-actions">
            {actions.map((action) => (
              <Tooltip key={action.key} title={action.tooltip}>
                <Button
                  shape="circle"
                  size="small"
                  className={action.variant ? `action-${action.variant}` : undefined}
                  onClick={action.onClick}
                  disabled={action.disabled}
                >
                  <Icon type={action.icon} />
                </Button>
              </Tooltip>
            ))}
          </div>
        )}
      </div>
    </AdCardHolder>
  )
}

export default AdCard
